import { MessageProcessor } from '@/services/websocketProcessorTypes'
import store from '@/redux/store'
import {
  updateTeamChatMessage,
  handleTeamChatMessagesLoaded,
  setWebSocketError,
  incrementTeamUnread,
} from '@/redux/slices/teamChatSlice'
import { ActiveTab } from '@/constants/chat'
import {
  addProposal,
  setProposals,
  setVoteCast,
  setVotingClosed,
} from '@/redux/slices/teamDecisionSlice'
import { TeamChatMessage } from '@/redux/types/teamChat'
import { TeamStrategyProposalItem } from '@/redux/types/teamDecision'

const normalizeVoteCounts = (
  voteCounts: Record<string | number, number> = {}
): Record<number, number> =>
  Object.entries(voteCounts).reduce(
    (acc, [proposalId, count]) => {
      acc[Number(proposalId)] = count
      return acc
    },
    {} as Record<number, number>
  )

export const teamChatProcessors: MessageProcessor[] = [
  {
    type: 'team_message',
    handler: (data) => {
      const state = store.getState()
      const isNew = !state.teamChat.messages.some(
        (m) => String(m.id) === String(data.id)
      )
      const isFromSelf = data.user === state.user.user?.email
      const isActive = state.chat.activeTab === ActiveTab.TeamRoom
      if (isNew && !isFromSelf && !isActive) {
        store.dispatch(incrementTeamUnread())
      }

      store.dispatch(
        updateTeamChatMessage({
          type: 'team',
          id: String(data.id),
          team_id: Number(data.team_id),
          user: data.user,
          message: data.message,
          name: data.name,
          sender_role: data.sender_role,
          user_profile_picture: data.user_profile_picture,
          entrytime: data.entrytime,
        })
      )
    },
  },
  {
    type: 'team_messages_loaded',
    handler: (data) => {
      const normalizedMessages = data.messages.map((msg: TeamChatMessage) => ({
        ...msg,
        id: String(msg.id),
        team_id: Number(msg.team_id),
      }))

      store.dispatch(
        handleTeamChatMessagesLoaded({
          messages: normalizedMessages,
          pagination: data.pagination,
        })
      )
    },
  },
  {
    type: 'team_chat_error',
    handler: (data) => {
      store.dispatch(setWebSocketError(data.error || 'Team chat error'))
    },
  },
  {
    type: 'proposal_added',
    handler: (data) => {
      if (!data.proposal) return
      store.dispatch(
        addProposal({
          proposal: data.proposal as TeamStrategyProposalItem,
        })
      )
    },
  },
  {
    type: 'proposals_loaded',
    handler: (data) => {
      store.dispatch(
        setProposals({
          proposals: (data.proposals || []) as TeamStrategyProposalItem[],
          voteCounts: normalizeVoteCounts(data.vote_counts || {}),
          votingClosed: Boolean(data.voting_closed),
          winningProposalId: data.winning_proposal_id ?? null,
          winningStrategyText: data.winning_strategy_text ?? null,
          myVoteProposalId: data.my_vote_proposal_id ?? null,
        })
      )
    },
  },
  {
    type: 'vote_cast',
    handler: (data) => {
      const state = store.getState()
      const currentUserEmail = state.user.user?.email
      const myVoteProposalId =
        currentUserEmail && data.voter_email === currentUserEmail
          ? Number(data.proposal_id)
          : undefined

      store.dispatch(
        setVoteCast({
          proposalId: Number(data.proposal_id),
          voterId: data.voter_id ? Number(data.voter_id) : undefined,
          voterEmail: data.voter_email,
          voteCounts: normalizeVoteCounts(data.vote_counts || {}),
          myVoteProposalId,
        })
      )
    },
  },
  {
    type: 'voting_closed',
    handler: (data) => {
      store.dispatch(
        setVotingClosed({
          winningProposalId: data.winning_proposal_id ?? null,
          winningStrategyText: data.winning_strategy_text ?? null,
          voteCounts: normalizeVoteCounts(data.vote_counts || {}),
        })
      )
    },
  },
]
